/**
 * The skies over the garden, and the marks they leave: this game's weather.
 *
 * Content, not engine. The timeline that decides *when* each sky happens is
 * `src/game/weather.ts` and knows nothing about any of this; what the skies
 * are, what they are called, how fast they grow things and which sourced card
 * each one hands over lives here.
 *
 * **Weather and mutation ids are derived back out of the timeline on every
 * load and stored in saves (`weatherSeen`, `mutationsSeen`) — never rename or
 * reuse one.** Before changing a `rate` or a weight, read the rules in
 * `AGENTS.md` ("Growth is a function of the clock"): no rate may be zero, and
 * `tests/weather.test.ts` checks every mark can still be earned.
 *
 * ## Grounding
 *
 * Three of the four are real conditions a dry-country garden actually has to
 * survive, and each carries the fact card that explains it — the mutation is
 * the teaching device. The fourth, golden hour, is tied to the player's own
 * local time and claims nothing factual, which is why it carries no card.
 */

export type WeatherId = 'clear' | 'rain' | 'sandstorm' | 'wadi-cool' | 'golden';

export type MutationId = 'rain-fed' | 'weathered' | 'wadi-cooled' | 'gilded';

export interface Mutation {
  id: MutationId;
  name: string;
  /** Multiplies the specimen's contribution to the register score. */
  bonus: number;
  /** Colour the foliage is pulled toward. */
  tint: number;
  /** How far toward `tint`, 0–1. */
  mix: number;
  /** Emissive strength, for the two that should catch light. */
  glow: number;
  detail: string;
}

export const MUTATIONS: Record<MutationId, Mutation> = {
  'rain-fed': {
    id: 'rain-fed',
    name: 'Rain-fed',
    bonus: 1.5,
    tint: 0x2f7a4e,
    mix: 0.32,
    glow: 0.04,
    detail: 'Grown through a seasonal downpour. Darker, denser foliage.',
  },
  weathered: {
    id: 'weathered',
    name: 'Weathered',
    bonus: 1.4,
    tint: 0xc9b894,
    mix: 0.34,
    glow: 0,
    detail: 'Grown through a sandstorm. Dust-bleached, and tougher for it.',
  },
  'wadi-cooled': {
    id: 'wadi-cooled',
    name: 'Wadi-cooled',
    bonus: 1.6,
    tint: 0x4fb3c4,
    mix: 0.28,
    glow: 0.05,
    detail: 'Grown in the cool that pools in a sunken wadi.',
  },
  gilded: {
    id: 'gilded',
    name: 'Gilded',
    bonus: 2,
    tint: 0xf0cd85,
    mix: 0.38,
    glow: 0.16,
    detail: 'Grown entirely inside golden hour. Two hours a day, and no more.',
  },
};

export interface Weather {
  id: WeatherId;
  name: string;
  glyph: string;
  /**
   * Growth-rate multiplier.
   *
   * Note that the sandstorm slows rather than stops. A true pause would mean
   * a player who happened to be away during one got *nothing* for that hour,
   * which reads as a punishment for being absent — the one thing this loop
   * refuses to do. Slowing is legible; stopping is punitive. It is also the
   * reason no rate is ever zero: `solveGrowTime` divides by it.
   */
  rate: number;
  /** One line for the HUD chip. */
  note: string;
  /** Sky and bounce colours the scene lighting eases toward. */
  sky: number;
  ground: number;
  /**
   * Zenith and horizon colours for the `preview` sky dome.
   *
   * Authored per weather rather than derived from `sky`. Deriving them was
   * the first attempt and it flattened every sky to the same beige: `sky` is
   * a *light* colour, chosen to tint foliage plausibly, and light colours
   * make terrible skies. A sunset needs a violet zenith against a burnt
   * orange horizon, and no arithmetic on a warm white gets you there.
   */
  skyTop: number;
  skyBottom: number;
  /** Overall light level relative to a clear day. */
  exposure: number;
  /** Falling-mote colour, or null for a still sky. */
  motes: number | null;
  /** What prolonged exposure marks a specimen with. */
  mutation: MutationId | null;
  /** The card explaining the real phenomenon behind it. */
  factId: string | null;
}

export const WEATHER: Record<WeatherId, Weather> = {
  clear: {
    id: 'clear',
    name: 'Clear',
    glyph: '☀️',
    rate: 1,
    note: 'Growing steadily',
    sky: 0xfff3dd,
    ground: 0xbfa77f,
    skyTop: 0x2e6ea8,
    skyBottom: 0xf5dcb0,
    exposure: 1,
    motes: null,
    mutation: null,
    factId: null,
  },
  rain: {
    id: 'rain',
    name: 'Seasonal rain',
    glyph: '🌧️',
    rate: 1.45,
    note: 'Growing 1.45× — the vaults are filling',
    sky: 0xcfe0e8,
    ground: 0x8fa08f,
    skyTop: 0x3c5266,
    skyBottom: 0x9fb3bd,
    exposure: 0.82,
    motes: 0x9ec8e0,
    mutation: 'rain-fed',
    factId: 'water',
  },
  sandstorm: {
    id: 'sandstorm',
    name: 'Sandstorm',
    glyph: '🌪️',
    rate: 0.7,
    note: 'Growing 0.7× — but a storm marks a tree',
    sky: 0xe8c98f,
    ground: 0xc4a271,
    skyTop: 0x9a6534,
    skyBottom: 0xe6b674,
    exposure: 0.74,
    motes: 0xd9bc8a,
    mutation: 'weathered',
    factId: 'ghaf-roots',
  },
  'wadi-cool': {
    id: 'wadi-cool',
    name: 'Wadi cool',
    glyph: '💧',
    rate: 1.3,
    note: 'Growing 1.3× — cooler in the shade down here',
    sky: 0xd6ecef,
    ground: 0x9ab89c,
    skyTop: 0x2b7f88,
    skyBottom: 0xdcecd9,
    exposure: 0.92,
    motes: null,
    mutation: 'wadi-cooled',
    factId: 'wadi',
  },
  golden: {
    id: 'golden',
    name: 'Golden hour',
    glyph: '🌇',
    // Cosmetic on purpose. Golden hour is pinned to the player's own clock
    // rather than rolled, so letting it alter pacing would mean the game ran
    // measurably faster for people who happen to play at six.
    rate: 1,
    note: 'The light is worth staying for',
    sky: 0xffd9a0,
    ground: 0xc98f52,
    skyTop: 0x3a2a5e,
    skyBottom: 0xff9a45,
    exposure: 1.06,
    motes: 0xf0cd85,
    mutation: 'gilded',
    factId: null,
  },
};

export const WEATHER_ORDER: WeatherId[] = [
  'clear',
  'rain',
  'wadi-cool',
  'sandstorm',
  'golden',
];

/**
 * The sky pinned to the player's own clock rather than rolled: golden hour,
 * over these local hours. The player's own sunrise and sunset.
 */
export const CLOCK_WEATHER: { id: WeatherId; hours: readonly number[] } = {
  id: 'golden',
  hours: [6, 18],
};

/** What the timeline falls back to if the draw weights do not reach 1. */
export const DEFAULT_WEATHER: WeatherId = 'clear';

/**
 * Draw weights for the rolled weathers. Golden is absent because it is not
 * rolled at all — it is whatever the player's own clock says.
 *
 * The sandstorm is the most common of the three for a non-obvious reason.
 * Slow weather stretches a plant's life, which dilutes its own share of it,
 * while fast weather shrinks the denominator and inflates its share. At an
 * earlier tuning — 0.45× and a 13% draw — the storm could never exceed a 25%
 * share of any specimen and the Weathered mutation was *literally
 * unreachable*: zero in four hundred sampled sidr. Rates and weights here are
 * balanced so that all four marks actually occur.
 */
export const WEATHER_DRAW: { id: WeatherId; weight: number }[] = [
  { id: 'clear', weight: 0.5 },
  { id: 'rain', weight: 0.15 },
  { id: 'wadi-cool', weight: 0.15 },
  { id: 'sandstorm', weight: 0.2 },
];
